/**
 * Admin Disputes Page - P5 Enhanced
 * 
 * Features:
 * - KPIs (open, needs response, $ at risk, win rate)
 * - Timeline view per dispute
 * - Ledger impact panel
 * - Risk hold management
 * - Quick actions (User 360, Entitlements)
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { ADMIN_ROUTES } from "../../config/adminRoutes";
import {
  getAdminDisputesSummary,
  listAdminDisputes,
  getAdminDisputeDetail,
  getAdminDisputeLedger,
  setAdminDisputeHold,
} from "../../api/adminDisputes";
import { ArrowLeft, RefreshCw, AlertTriangle, Shield, X, ExternalLink } from "lucide-react";

const formatMoney = (cents, currency = "usd") => {
  const v = (Number(cents || 0) / 100).toFixed(2);
  return `$${v}`;
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const due = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function AdminDisputesPage() {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [days, setDays] = useState(30);
  const [kpis, setKpis] = useState(null);
  const [tab, setTab] = useState("open");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [holdWorking, setHoldWorking] = useState(false);

  const isAdmin = !!user?.is_admin;

  const fetchList = React.useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const res = await listAdminDisputes(token, { status: tab, page, page_size: pageSize });
      setItems(res?.items || []);
      setTotal(Number(res?.total || 0));
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "Failed to load disputes.");
    } finally {
      setLoading(false);
    }
  }, [token, tab, page, pageSize]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await getAdminDisputesSummary(token, days);
        setKpis(res?.kpis || null);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [token, isAdmin, days]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchList();
  }, [isAdmin, fetchList]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  const openDrawer = async (row) => {
    setSelected(row);
    setDetail(null);
    setLedger(null);
    setDrawerOpen(true);
    try {
      const d = await getAdminDisputeDetail(token, row?.dispute_id || row?.id);
      setDetail(d?.dispute || null);
    } catch (e) {
      console.error(e);
    }
    try {
      const l = await getAdminDisputeLedger(token, row?.dispute_id || row?.id);
      setLedger(l || null);
    } catch (e) {
      console.error(e);
    }
  };

  const setHold = async (action) => {
    if (!detail?.dispute_id && !selected?.dispute_id) return;
    setHoldWorking(true);
    try {
      const id = detail?.dispute_id || selected?.dispute_id;
      const res = await setAdminDisputeHold(token, {
        dispute_id: id,
        action,
        note: action === "apply" ? "Admin applied risk hold" : "Admin cleared risk hold",
      });
      const newHold = !!res?.risk_hold;
      setDetail((prev) => (prev ? { ...prev, risk_hold: newHold } : prev));
      setItems((prev) =>
        (prev || []).map((x) => (x.dispute_id === id ? { ...x, risk_hold: newHold } : x))
      );
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Failed to update hold.");
    } finally {
      setHoldWorking(false);
    }
  };

  const Tabs = [
    { key: "open", label: "Open" },
    { key: "needs_response", label: "Needs Response" },
    { key: "under_review", label: "Under Review" },
    { key: "won", label: "Won" },
    { key: "lost", label: "Lost" },
    { key: "canceled", label: "Canceled" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => navigate(ADMIN_ROUTES.DASHBOARD)}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </button>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-rose-400 to-amber-500 bg-clip-text text-transparent">
              Disputes
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Chargebacks & disputes • KPIs, timelines, ledger impact, risk holds
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
            <button
              onClick={fetchList}
              disabled={loading}
              className="flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
            <div className="text-xs text-slate-500 mb-1">Open disputes</div>
            <div className="text-2xl font-bold text-slate-100">{kpis?.open_count ?? "—"}</div>
          </div>
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4">
            <div className="text-xs text-slate-500 mb-1">$ at risk</div>
            <div className="text-2xl font-bold text-rose-400">
              {kpis ? formatMoney(kpis.at_risk_cents) : "—"}
            </div>
          </div>
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="text-xs text-slate-500 mb-1">Needs response</div>
            <div className="text-2xl font-bold text-amber-400">{kpis?.needs_response_count ?? "—"}</div>
          </div>
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
            <div className="text-xs text-slate-500 mb-1">Win rate</div>
            <div className="text-2xl font-bold text-emerald-400">
              {kpis ? `${Math.round((kpis.win_rate || 0) * 100)}%` : "—"}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-xl border text-sm font-medium transition ${
                tab === t.key
                  ? "bg-rose-500/20 border-rose-500/40 text-rose-300"
                  : "bg-slate-900/50 border-slate-800 text-slate-400 hover:bg-slate-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-400">
            {error}
          </div>
        )}

        {/* List */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <div className="text-sm text-slate-400">
              {loading ? "Loading…" : `${total} total`}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-800/50 text-slate-400">
                <tr>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-left px-4 py-3">Evidence due</th>
                  <th className="text-left px-4 py-3">Hold</th>
                </tr>
              </thead>
              <tbody>
                {(items || []).map((row) => {
                  const dl = daysLeft(row?.evidence_due_by);
                  return (
                    <tr
                      key={row?.dispute_id || row?.id}
                      className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer transition"
                      onClick={() => openDrawer(row)}
                    >
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full border border-slate-700 bg-slate-800/50 px-2 py-0.5 text-xs">
                          {row?.status || "unknown"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{formatMoney(row?.amount_cents, row?.currency)}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-200">{row?.user?.display_name || "—"}</div>
                        <div className="text-xs text-slate-500">{row?.user?.email || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{row?.reason || "—"}</td>
                      <td className="px-4 py-3">
                        {row?.evidence_due_by ? (
                          <span className={`text-xs ${dl !== null && dl <= 2 ? "text-rose-400" : "text-slate-400"}`}>
                            {new Date(row.evidence_due_by).toLocaleDateString()}
                            {dl !== null ? ` • ${dl}d left` : ""}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row?.risk_hold ? (
                          <span className="text-xs text-amber-400 font-medium">ON</span>
                        ) : (
                          <span className="text-xs text-slate-600">OFF</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && (!items || items.length === 0) && (
                  <tr>
                    <td className="px-4 py-8 text-slate-500 text-center" colSpan={6}>
                      No disputes found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-slate-800 flex items-center justify-between">
            <div className="text-xs text-slate-500">
              Page {page} / {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-40 transition"
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-40 transition"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-slate-950 border-l border-slate-800 p-4 overflow-y-auto">
            {/* Drawer Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-sm text-slate-500">Dispute</div>
                <div className="text-lg font-semibold break-all">{detail?.dispute_id || selected?.dispute_id}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Status: <span className="text-slate-300">{detail?.status || selected?.status || "unknown"}</span>
                </div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="rounded-lg border border-slate-700 bg-slate-800/50 p-2 hover:bg-slate-800 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Overview */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Amount</div>
                  <div className="font-semibold text-rose-400">{formatMoney(detail?.amount_cents || selected?.amount_cents)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Reason</div>
                  <div className="text-slate-300">{detail?.reason || selected?.reason || "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-slate-500 mb-1">Evidence due</div>
                  <div className="text-slate-300">
                    {detail?.evidence_due_by ? new Date(detail.evidence_due_by).toLocaleString() : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
              <div className="font-semibold mb-3">Timeline</div>
              <div className="space-y-2">
                {(detail?.timeline || []).map((t) => (
                  <div key={t.key} className="flex items-center justify-between text-sm">
                    <div className="text-slate-300">{t.label}</div>
                    <div className="text-xs text-slate-500">{t.at ? new Date(t.at).toLocaleString() : ""}</div>
                  </div>
                ))}
                {!detail?.timeline?.length && <div className="text-sm text-slate-600">—</div>}
              </div>
            </div>

            {/* Ledger Impact */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold">Ledger Impact</div>
                <div className="text-xs text-slate-500">{ledger?.impact?.count ?? "—"} entries</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                <div className="rounded-xl border border-slate-800 bg-slate-800/50 p-3">
                  <div className="text-xs text-slate-500">Net</div>
                  <div className="font-semibold">{ledger ? formatMoney(ledger.impact?.net_cents) : "—"}</div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-800/50 p-3">
                  <div className="text-xs text-slate-500">Credits</div>
                  <div className="font-semibold text-emerald-400">{ledger ? formatMoney(ledger.impact?.credits_cents) : "—"}</div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-800/50 p-3">
                  <div className="text-xs text-slate-500">Debits</div>
                  <div className="font-semibold text-rose-400">{ledger ? formatMoney(ledger.impact?.debits_cents) : "—"}</div>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-slate-800 rounded-xl">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-800/50 text-slate-400">
                    <tr>
                      <th className="text-left px-3 py-2">Time</th>
                      <th className="text-left px-3 py-2">Product</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ledger?.entries || []).map((e) => (
                      <tr key={e.id} className="border-t border-slate-800">
                        <td className="px-3 py-2 text-slate-500">{new Date(e.created_at).toLocaleDateString()}</td>
                        <td className="px-3 py-2 text-slate-300">{e.product || "—"}</td>
                        <td className="px-3 py-2 text-slate-500">{e.type || "—"}</td>
                        <td className="px-3 py-2 text-right">{formatMoney(e.amount_cents)}</td>
                      </tr>
                    ))}
                    {!ledger?.entries?.length && (
                      <tr>
                        <td className="px-3 py-4 text-slate-600" colSpan={4}>
                          No related ledger entries found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
              <div className="font-semibold mb-3">Quick Actions</div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    const uid = selected?.user?.id || detail?.raw?.user_id;
                    if (uid) navigate(ADMIN_ROUTES.USER_360(uid));
                  }}
                  className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm hover:bg-slate-800 transition"
                >
                  <ExternalLink className="w-3 h-3" />
                  User 360
                </button>
                <button
                  onClick={() => {
                    const uid = selected?.user?.id || detail?.raw?.user_id;
                    if (uid) navigate(ADMIN_ROUTES.USER_ENTITLEMENTS(uid));
                  }}
                  className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm hover:bg-slate-800 transition"
                >
                  <Shield className="w-3 h-3" />
                  Entitlements
                </button>
                <button
                  disabled={holdWorking}
                  onClick={() => setHold("apply")}
                  className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-400 hover:bg-amber-500/20 disabled:opacity-40 transition"
                >
                  <AlertTriangle className="w-3 h-3" />
                  Apply Hold
                </button>
                <button
                  disabled={holdWorking}
                  onClick={() => setHold("clear")}
                  className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-40 transition"
                >
                  Clear Hold
                </button>
              </div>
              <div className="text-xs text-slate-600 mt-3">
                Recommended: Revoke entitlements when dispute opens (strict). Or hold-only until resolution.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
